function nowIso() {
  return new Date().toISOString()
}

function randomId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `id_${Math.random().toString(16).slice(2)}_${Date.now()}`
}

function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function createEmptyAppState() {
  return {
    segmentations: {},
    elements: {},
    dynamicRules: {},
    groupConfigs: {},
    groupConfigVersions: {},
    groupSegments: {},
    elementBindings: {},
    abTests: {},
    abVariants: {},
    abVariantElementBindings: {},
  }
}

function normalizeString(value) {
  return String(value || '').trim()
}

function createInitialState() {
  return {
    schemaVersion: 4,
    applications: {},
  }
}

let _state = createInitialState()
const _listeners = new Set()

function emit() {
  for (const listener of _listeners) listener()
}

export function subscribe(listener) {
  _listeners.add(listener)
  return () => _listeners.delete(listener)
}

export function getSnapshot() {
  return _state
}

function setState(nextState) {
  _state = nextState
  emit()
}

function ensureApp(state, applicationId) {
  if (!state.applications[applicationId]) {
    state.applications[applicationId] = createEmptyAppState()
  }
  return state.applications[applicationId]
}

function ensureDefaultSegmentation(appState, applicationId) {
  const existingDefault = Object.values(appState.segmentations).find((seg) => seg.isDefault)
  if (existingDefault) return existingDefault

  const segId = randomId()
  const created = {
    id: segId,
    applicationId,
    name: 'Default',
    description: '',
    isDefault: true,
    requiredPatternTypes: [],
    patterns: [],
    createdAt: nowIso(),
    updatedAt: nowIso(),
  }
  appState.segmentations[segId] = created
  return created
}

function getGroupConfigDraftVersion(appState, groupConfigId) {
  const normalizedGroupConfigId = String(groupConfigId || '').trim()
  const versions = Object.values(appState.groupConfigVersions).filter(
    (version) => String(version.groupConfigId || '').trim() === normalizedGroupConfigId,
  )
  const draftVersions = versions.filter((version) => version.status === 'draft')
  draftVersions.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
  return draftVersions[0] || null
}

function resolveTargetGroupConfigVersionId(appState, groupConfigId, targetVersionId) {
  const normalizedTargetVersionId = String(targetVersionId || '').trim()
  if (normalizedTargetVersionId && appState.groupConfigVersions[normalizedTargetVersionId]) {
    return normalizedTargetVersionId
  }
  const draft = getGroupConfigDraftVersion(appState, groupConfigId)
  return draft?.id || ''
}

function getNextPriority(appState, groupConfigVersionId) {
  const segments = Object.values(appState.groupSegments).filter(
    (seg) => seg.groupConfigVersionId === groupConfigVersionId,
  )
  if (segments.length === 0) return 1
  return Math.max(...segments.map((seg) => seg.priority || 0)) + 1
}

function normalizePriorities(appState, groupConfigVersionId) {
  const segments = Object.values(appState.groupSegments)
    .filter((seg) => seg.groupConfigVersionId === groupConfigVersionId)
    .sort((a, b) => (a.priority || 0) - (b.priority || 0))

  segments.forEach((seg, idx) => {
    seg.priority = idx + 1
  })
}

function ensureSingleDefaultGroupSegment(appState, groupConfigVersionId, defaultGroupSegmentId) {
  for (const seg of Object.values(appState.groupSegments)) {
    if (seg.groupConfigVersionId !== groupConfigVersionId) continue
    seg.isDefault = seg.id === defaultGroupSegmentId
  }
}

function cloneVersionGraph(appState, fromVersionId, toVersionId) {
  const fromSegments = Object.values(appState.groupSegments).filter(
    (seg) => seg.groupConfigVersionId === fromVersionId,
  )
  const segmentIdMap = new Map()

  for (const seg of fromSegments) {
    const newId = randomId()
    segmentIdMap.set(seg.id, newId)
    appState.groupSegments[newId] = {
      ...clone(seg),
      id: newId,
      groupConfigVersionId: toVersionId,
    }
  }

  const fromBindings = Object.values(appState.elementBindings).filter((binding) =>
    segmentIdMap.has(binding.groupSegmentId),
  )

  for (const binding of fromBindings) {
    const newId = randomId()
    appState.elementBindings[newId] = {
      ...clone(binding),
      id: newId,
      groupSegmentId: segmentIdMap.get(binding.groupSegmentId),
    }
  }
}

function getElementUsageCount(appState, elementId) {
  let count = 0
  for (const binding of Object.values(appState.elementBindings)) {
    if (binding.elementId === elementId) count += 1
  }
  return count
}

function getElementUsageCountInAbTests(appState, elementId) {
  let count = 0
  for (const binding of Object.values(appState.abVariantElementBindings || {})) {
    if (binding.elementId === elementId) count += 1
  }
  return count
}

function normalizeDynamicRuleIds(appState, dynamicRuleIds) {
  if (!Array.isArray(dynamicRuleIds)) return []

  const uniqueIds = new Set(
    dynamicRuleIds
      .map((id) => String(id || '').trim())
      .filter(Boolean),
  )

  return Array.from(uniqueIds).filter((id) => Boolean(appState.dynamicRules?.[id]))
}

function getDynamicRuleUsageCount(appState, dynamicRuleId) {
  let count = 0
  for (const element of Object.values(appState.elements || {})) {
    if (Array.isArray(element.dynamicRuleIds) && element.dynamicRuleIds.includes(dynamicRuleId)) {
      count += 1
    }
  }
  return count
}

function normalizeAbVariantOrder(appState, abTestId) {
  const variants = Object.values(appState.abVariants || {})
    .filter((v) => v.abTestId === abTestId)
    .sort((a, b) => (a.order || 0) - (b.order || 0))

  variants.forEach((variant, idx) => {
    variant.order = idx + 1
  })
}

function normalizeAbVariantTraffic(appState, abTestId) {
  const variants = Object.values(appState.abVariants || {})
    .filter((v) => v.abTestId === abTestId)
    .sort((a, b) => (a.order || 0) - (b.order || 0))

  if (variants.length === 0) return
  const even = Math.floor(100 / variants.length)
  let remainder = 100 - even * variants.length

  for (const variant of variants) {
    variant.trafficPercent = even + (remainder > 0 ? 1 : 0)
    remainder = Math.max(0, remainder - 1)
  }
}

export function actions() {
  return {
    createGroupConfig(applicationId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)

      const groupConfigId = randomId()
      const createdAt = nowIso()

      appState.groupConfigs[groupConfigId] = {
        id: groupConfigId,
        applicationId,
        name: String(values?.name || '').trim(),
        description: String(values?.description || '').trim(),
        startBundleVersion: String(values?.startBundleVersion || '').trim(),
        createdAt,
        updatedAt: createdAt,
      }

      const versionId = randomId()
      appState.groupConfigVersions[versionId] = {
        id: versionId,
        applicationId,
        groupConfigId,
        status: 'draft',
        createdAt,
        updatedAt: createdAt,
        parentVersionId: null,
      }

      const defaultSegmentation = ensureDefaultSegmentation(appState, applicationId)
      const groupSegmentId = randomId()
      appState.groupSegments[groupSegmentId] = {
        id: groupSegmentId,
        applicationId,
        groupConfigVersionId: versionId,
        segmentKey: randomId(),
        segmentationId: defaultSegmentation.id,
        priority: 1,
        isEnabled: true,
        isDefault: true,
        createdAt,
        updatedAt: createdAt,
      }

      setState(next)
      return { groupConfigId, versionId }
    },

    hydrateGroupConfigDraft(applicationId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const createdAt = nowIso()

      const groupConfigId = String(values?.groupConfigId || '').trim()
      const versionId = String(values?.draftRemoteConfigId || '').trim()
      const segmentationId = String(values?.defaultSegmentationId || '').trim()
      const groupSegmentId = String(values?.defaultGroupSegmentId || '').trim()

      if (!groupConfigId || !versionId || !segmentationId || !groupSegmentId) {
        throw new Error('Cannot open builder: missing default draft ids.')
      }

      appState.groupConfigs[groupConfigId] = {
        id: groupConfigId,
        applicationId,
        name: String(values?.name || '').trim(),
        description: String(values?.description || '').trim(),
        startBundleVersion: String(values?.startBundleVersion || '').trim(),
        createdAt: values?.createdAt || createdAt,
        updatedAt: values?.updatedAt || createdAt,
      }

      appState.groupConfigVersions[versionId] = {
        id: versionId,
        applicationId,
        groupConfigId,
        status: 'draft',
        createdAt,
        updatedAt: createdAt,
        parentVersionId: null,
      }

      const defaultSegmentation = values?.defaultSegmentation
      if (!appState.segmentations[segmentationId]) {
        appState.segmentations[segmentationId] = {
          id: segmentationId,
          applicationId,
          name: normalizeString(defaultSegmentation?.name) || 'All users',
          description:
            normalizeString(defaultSegmentation?.description) ||
            'Default segment created automatically',
          isDefault: defaultSegmentation?.isDefault ?? true,
          requiredPatternTypes: Array.isArray(defaultSegmentation?.requiredPatternTypes)
            ? defaultSegmentation.requiredPatternTypes
            : [],
          patterns: Array.isArray(defaultSegmentation?.patterns)
            ? defaultSegmentation.patterns
            : [],
          createdAt: defaultSegmentation?.createdAt || createdAt,
          updatedAt: defaultSegmentation?.updatedAt || createdAt,
        }
      }

      appState.groupSegments[groupSegmentId] = {
        id: groupSegmentId,
        applicationId,
        groupConfigVersionId: versionId,
        segmentKey: randomId(),
        segmentationId,
        priority: 1,
        isEnabled: true,
        isDefault: true,
        createdAt,
        updatedAt: createdAt,
      }

      setState(next)
      return { groupConfigId, versionId, groupSegmentId }
    },

    hydrateGroupSegmentsForDraft(applicationId, groupConfigId, segments, targetVersionId = null) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const versionId = resolveTargetGroupConfigVersionId(appState, groupConfigId, targetVersionId)
      if (!versionId) return 0
      const incoming = Array.isArray(segments) ? segments : []
      const incomingSegmentIds = new Set(incoming.map((item) => String(item?.id || '').trim()).filter(Boolean))

      for (const groupSegment of Object.values(appState.groupSegments)) {
        if (groupSegment.groupConfigVersionId !== versionId) continue

        const groupSegmentId = String(groupSegment.id || '').trim()
        if (incomingSegmentIds.has(groupSegmentId)) continue

        for (const binding of Object.values(appState.elementBindings)) {
          if (binding.groupSegmentId === groupSegment.id) {
            delete appState.elementBindings[binding.id]
          }
        }

        delete appState.groupSegments[groupSegment.id]
      }

      const createdAt = nowIso()
      for (const item of incoming) {
        const id = String(item?.id || '').trim()
        const segmentationId = String(item?.segmentationId || '').trim()
        if (!id || !segmentationId) continue

        appState.groupSegments[id] = {
          id,
          applicationId,
          groupConfigVersionId: versionId,
          segmentKey: String(item?.segmentKey || '').trim() || randomId(),
          segmentationId,
          priority: Number.isFinite(item?.priority) ? item.priority : 1,
          isEnabled: item?.isEnabled !== undefined ? Boolean(item.isEnabled) : true,
          isDefault: item?.isDefault !== undefined ? Boolean(item.isDefault) : false,
          createdAt: item?.createdAt || createdAt,
          updatedAt: item?.updatedAt || createdAt,
        }
      }

      normalizePriorities(appState, versionId)
      const orderedSegments = Object.values(appState.groupSegments)
        .filter((seg) => seg.groupConfigVersionId === versionId)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
      const currentDefault = orderedSegments.find((seg) => seg.isDefault)
      if (currentDefault) {
        ensureSingleDefaultGroupSegment(appState, versionId, currentDefault.id)
      } else if (orderedSegments.length > 0) {
        ensureSingleDefaultGroupSegment(appState, versionId, orderedSegments[0].id)
      }

      setState(next)
      return orderedSegments.length
    },

    hydrateElementBindingsForDraft(applicationId, groupConfigId, bindings, targetVersionId = null) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const versionId = resolveTargetGroupConfigVersionId(appState, groupConfigId, targetVersionId)
      if (!versionId) return 0

      const draftSegmentIds = new Set(
        Object.values(appState.groupSegments)
          .filter((seg) => seg.groupConfigVersionId === versionId)
          .map((seg) => String(seg.id || '').trim())
          .filter(Boolean),
      )

      for (const existingBinding of Object.values(appState.elementBindings)) {
        if (!draftSegmentIds.has(String(existingBinding.groupSegmentId || '').trim())) continue
        delete appState.elementBindings[existingBinding.id]
      }

      const incoming = Array.isArray(bindings) ? bindings : []
      const createdAt = nowIso()
      for (const binding of incoming) {
        const bindingId = String(binding?.id || '').trim()
        const groupSegmentId = String(binding?.groupSegmentId || '').trim()
        const elementId = binding?.elementId
        if (!bindingId || !groupSegmentId || !draftSegmentIds.has(groupSegmentId)) continue

        appState.elementBindings[bindingId] = {
          id: bindingId,
          applicationId,
          groupSegmentId,
          elementId,
          conditionExpression: String(binding?.conditionExpression || '').trim(),
          order: Number.isFinite(binding?.order) ? binding.order : 1,
          createdAt: binding?.createdAt || createdAt,
          updatedAt: binding?.updatedAt || createdAt,
        }
      }

      setState(next)
      return Object.values(appState.elementBindings).filter((item) =>
        draftSegmentIds.has(String(item.groupSegmentId || '').trim()),
      ).length
    },

    hydrateSegmentations(applicationId, segmentations) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const incoming = Array.isArray(segmentations) ? segmentations : []
      const updatedAt = nowIso()

      for (const segmentation of incoming) {
        const id = normalizeString(segmentation?.id)
        if (!id) continue
        appState.segmentations[id] = {
          ...appState.segmentations[id],
          id,
          applicationId,
          groupConfigId: segmentation?.groupConfigId ?? appState.segmentations[id]?.groupConfigId ?? null,
          groupConfig: segmentation?.groupConfig ?? appState.segmentations[id]?.groupConfig ?? null,
          name: normalizeString(segmentation?.name),
          description: normalizeString(segmentation?.description),
          isDefault: Boolean(segmentation?.isDefault),
          requiredPatternTypes: Array.isArray(segmentation?.requiredPatternTypes)
            ? segmentation.requiredPatternTypes
            : [],
          patterns: Array.isArray(segmentation?.patterns) ? segmentation.patterns : [],
          createdAt: segmentation?.createdAt || appState.segmentations[id]?.createdAt || updatedAt,
          updatedAt: segmentation?.updatedAt || updatedAt,
        }
      }

      setState(next)
      return incoming.length
    },

    hydrateElements(applicationId, elements) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const incoming = Array.isArray(elements) ? elements : []
      const updatedAt = nowIso()

      for (const element of incoming) {
        const id = normalizeString(element?.id)
        if (!id) continue
        appState.elements[id] = {
          ...appState.elements[id],
          id,
          applicationId,
          groupConfigId: element?.groupConfigId || null,
          name: normalizeString(element?.name),
          elementType: normalizeString(element?.elementType) || 'advertising',
          description: normalizeString(element?.description),
          jsonConfig: element?.jsonConfig ?? null,
          rawJsonConfig: normalizeString(element?.rawJsonConfig),
          text: String(element?.text || ''),
          version: Number.isFinite(element?.version) ? element.version : 1,
          status: normalizeString(element?.status) || 'draft',
          publishedAt: element?.publishedAt || null,
          archivedAt: element?.archivedAt || null,
          createdAt: element?.createdAt || appState.elements[id]?.createdAt || updatedAt,
          updatedAt: element?.updatedAt || updatedAt,
        }
      }

      setState(next)
      return incoming.length
    },

    hydrateAbTests(applicationId, abTests) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const incoming = Array.isArray(abTests) ? abTests : []
      const updatedAt = nowIso()
      const incomingTestIds = new Set()

      for (const test of incoming) {
        const id = normalizeString(test?.id)
        if (!id) continue
        incomingTestIds.add(id)
        appState.abTests[id] = {
          ...appState.abTests[id],
          id,
          applicationId,
          groupConfigId: test?.groupConfigId,
          groupSegmentId: test?.groupSegmentId ?? appState.abTests[id]?.groupSegmentId ?? null,
          segmentationId: test?.segmentationId ?? appState.abTests[id]?.segmentationId ?? null,
          segmentKey: normalizeString(test?.segmentKey ?? test?.segmentationId),
          name: normalizeString(test?.name),
          description: normalizeString(test?.description),
          status: normalizeString(test?.status) || 'draft',
          isEnabled: Boolean(test?.isEnabled),
          isOnlyNewUser: Boolean(test?.isOnlyNewUser),
          automaticDistributionStop: Boolean(test?.automaticDistributionStop),
          releaseAllVersions: Boolean(test?.releaseAllVersions),
          audience: Number.isFinite(test?.audience) ? test.audience : null,
          targetMetric: normalizeString(test?.targetMetric),
          metricDays: Number.isFinite(test?.metricDays) ? test.metricDays : null,
          targetUserCount: Number.isFinite(test?.targetUserCount) ? test.targetUserCount : null,
          startedAt: test?.startedAt || null,
          finishedAt: test?.finishedAt || null,
          createdAt: test?.createdAt || appState.abTests[id]?.createdAt || updatedAt,
          updatedAt: test?.updatedAt || updatedAt,
        }
      }

      for (const existingVariant of Object.values(appState.abVariants || {})) {
        if (!incomingTestIds.has(String(existingVariant.abTestId || '').trim())) continue
        delete appState.abVariants[existingVariant.id]
      }

      for (const existingBinding of Object.values(appState.abVariantElementBindings || {})) {
        const variantId = String(existingBinding.abVariantId || '').trim()
        const variant = appState.abVariants?.[variantId]
        if (variant) continue
        delete appState.abVariantElementBindings[existingBinding.id]
      }

      for (const test of incoming) {
        const testId = normalizeString(test?.id)
        if (!testId) continue
        const variants = Array.isArray(test?.variants) ? test.variants : []
        for (const variant of variants) {
          const variantId = normalizeString(variant?.id)
          if (!variantId) continue
          appState.abVariants[variantId] = {
            ...appState.abVariants[variantId],
            id: variantId,
            applicationId,
            abTestId: testId,
            name: normalizeString(variant?.name),
            order: Number.isFinite(variant?.order) ? variant.order : 1,
            trafficPercent: Number.isFinite(variant?.trafficPercent) ? variant.trafficPercent : 0,
            createdAt: variant?.createdAt || appState.abVariants[variantId]?.createdAt || updatedAt,
            updatedAt: variant?.updatedAt || updatedAt,
          }

          const elements = Array.isArray(variant?.elements) ? variant.elements : []
          for (const binding of elements) {
            const bindingId = normalizeString(binding?.id)
            const elementId = normalizeString(binding?.elementId)
            if (!bindingId || !elementId) continue
            appState.abVariantElementBindings[bindingId] = {
              ...appState.abVariantElementBindings[bindingId],
              id: bindingId,
              applicationId,
              abVariantId: variantId,
              elementId,
              conditionExpression: normalizeString(binding?.conditionExpression),
              order: Number.isFinite(binding?.order) ? binding.order : 1,
              createdAt:
                binding?.createdAt ||
                appState.abVariantElementBindings[bindingId]?.createdAt ||
                updatedAt,
              updatedAt: binding?.updatedAt || updatedAt,
            }
          }
        }
      }

      setState(next)
      return incoming.length
    },

    updateGroupConfig(applicationId, groupConfigId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const existing = appState.groupConfigs[groupConfigId]
      if (!existing) return null

      existing.name = String(values?.name ?? existing.name).trim()
      existing.description = String(values?.description ?? existing.description).trim()
      existing.startBundleVersion = String(
        values?.startBundleVersion ?? existing.startBundleVersion,
      ).trim()
      existing.updatedAt = nowIso()

      setState(next)
      return groupConfigId
    },

    createSegmentation(applicationId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const segId = randomId()
      const createdAt = nowIso()

      appState.segmentations[segId] = {
        id: segId,
        applicationId,
        name: String(values?.name || '').trim(),
        description: String(values?.description || '').trim(),
        isDefault: Boolean(values?.isDefault),
        requiredPatternTypes: Array.isArray(values?.requiredPatternTypes)
          ? values.requiredPatternTypes
          : [],
        patterns: Array.isArray(values?.patterns) ? values.patterns : [],
        createdAt,
        updatedAt: createdAt,
      }

      if (appState.segmentations[segId].isDefault) {
        for (const seg of Object.values(appState.segmentations)) {
          if (seg.id !== segId) seg.isDefault = false
        }
      }

      setState(next)
      return segId
    },

    updateSegmentation(applicationId, segmentationId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const seg = appState.segmentations[segmentationId]
      if (!seg) return null

      if (values?.name !== undefined) seg.name = String(values.name || '').trim()
      if (values?.description !== undefined) seg.description = String(values.description || '').trim()
      if (Array.isArray(values?.patterns)) seg.patterns = values.patterns
      if (Array.isArray(values?.requiredPatternTypes)) seg.requiredPatternTypes = values.requiredPatternTypes

      if (values?.isDefault === true) {
        for (const other of Object.values(appState.segmentations)) {
          other.isDefault = other.id === segmentationId
        }
      }

      seg.updatedAt = nowIso()
      setState(next)
      return segmentationId
    },

    deleteSegmentation(applicationId, segmentationId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const seg = appState.segmentations[segmentationId]
      if (!seg) return null
      if (seg.isDefault) {
        throw new Error('Cannot delete default segmentation')
      }

      for (const gs of Object.values(appState.groupSegments)) {
        if (gs.segmentationId === segmentationId) {
          throw new Error('Cannot delete segmentation that is used in a group config')
        }
      }

      delete appState.segmentations[segmentationId]
      setState(next)
      return segmentationId
    },

    createAbTest(applicationId, groupConfigId, segmentKey, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const createdAt = nowIso()

      const abTestId = randomId()
      const status = String(values?.status || 'draft')
      if (!['draft', 'testing', 'release'].includes(status)) {
        throw new Error('Invalid AB test status')
      }

      appState.abTests[abTestId] = {
        id: abTestId,
        applicationId,
        groupConfigId,
        segmentKey,
        name: String(values?.name || '').trim(),
        description: String(values?.description || '').trim(),
        status,
        isEnabled: true,
        createdAt,
        updatedAt: createdAt,
      }

      const variantAId = randomId()
      const variantBId = randomId()
      appState.abVariants[variantAId] = {
        id: variantAId,
        applicationId,
        abTestId,
        name: 'A',
        order: 1,
        trafficPercent: 50,
        createdAt,
        updatedAt: createdAt,
      }
      appState.abVariants[variantBId] = {
        id: variantBId,
        applicationId,
        abTestId,
        name: 'B',
        order: 2,
        trafficPercent: 50,
        createdAt,
        updatedAt: createdAt,
      }

      setState(next)
      return abTestId
    },

    setAbTestStatus(applicationId, abTestId, status) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const abTest = appState.abTests?.[abTestId]
      if (!abTest) return null
      if (!['draft', 'testing', 'release'].includes(status)) {
        throw new Error('Invalid AB test status')
      }
      abTest.status = status
      abTest.updatedAt = nowIso()
      setState(next)
      return abTestId
    },

    setAbTestEnabled(applicationId, abTestId, isEnabled) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const abTest = appState.abTests?.[abTestId]
      if (!abTest) return null
      abTest.isEnabled = Boolean(isEnabled)
      abTest.updatedAt = nowIso()
      setState(next)
      return abTestId
    },

    updateAbTest(applicationId, abTestId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const abTest = appState.abTests?.[abTestId]
      if (!abTest) return null
      if (values?.name !== undefined) abTest.name = String(values.name || '').trim()
      if (values?.description !== undefined) abTest.description = String(values.description || '').trim()
      if (values?.groupConfigId !== undefined) {
        abTest.groupConfigId = values.groupConfigId
      }
      if (values?.segmentationId !== undefined) {
        abTest.segmentationId = values.segmentationId ? String(values.segmentationId).trim() : null
        abTest.segmentKey = abTest.segmentationId || ''
      }
      if (values?.segmentKey !== undefined) {
        abTest.segmentKey = String(values.segmentKey || '').trim()
      }
      if (values?.groupSegmentId !== undefined) {
        abTest.groupSegmentId = values.groupSegmentId ? String(values.groupSegmentId).trim() : null
      }
      if (values?.audience !== undefined) {
        abTest.audience = Number.isFinite(values.audience) ? values.audience : null
      }
      if (values?.targetMetric !== undefined) {
        abTest.targetMetric = String(values.targetMetric || '').trim()
      }
      if (values?.metricDays !== undefined) {
        abTest.metricDays = Number.isFinite(values.metricDays) ? values.metricDays : null
      }
      if (values?.targetUserCount !== undefined) {
        abTest.targetUserCount = Number.isFinite(values.targetUserCount) ? values.targetUserCount : null
      }
      if (values?.isOnlyNewUser !== undefined) {
        abTest.isOnlyNewUser = Boolean(values.isOnlyNewUser)
      }
      if (values?.automaticDistributionStop !== undefined) {
        abTest.automaticDistributionStop = Boolean(values.automaticDistributionStop)
      }
      if (values?.releaseAllVersions !== undefined) {
        abTest.releaseAllVersions = Boolean(values.releaseAllVersions)
      }
      if (values?.testFinishDate !== undefined) {
        abTest.testFinishDate = values.testFinishDate || null
      }
      abTest.updatedAt = nowIso()
      setState(next)
      return abTestId
    },

    addAbVariant(applicationId, abTestId, name) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      if (!appState.abTests?.[abTestId]) return null

      const createdAt = nowIso()
      const existing = Object.values(appState.abVariants || {}).filter((v) => v.abTestId === abTestId)
      const nextOrder = existing.length ? Math.max(...existing.map((v) => v.order || 0)) + 1 : 1

      const id = randomId()
      appState.abVariants[id] = {
        id,
        applicationId,
        abTestId,
        name: String(name || '').trim() || `V${nextOrder}`,
        order: nextOrder,
        trafficPercent: 0,
        createdAt,
        updatedAt: createdAt,
      }

      normalizeAbVariantOrder(appState, abTestId)
      normalizeAbVariantTraffic(appState, abTestId)
      setState(next)
      return id
    },

    removeAbVariant(applicationId, variantId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const variant = appState.abVariants?.[variantId]
      if (!variant) return null
      const abTestId = variant.abTestId

      const remaining = Object.values(appState.abVariants || {}).filter((v) => v.abTestId === abTestId)
      if (remaining.length <= 2) throw new Error('AB test must have at least 2 variants')

      for (const binding of Object.values(appState.abVariantElementBindings || {})) {
        if (binding.abVariantId === variantId) {
          delete appState.abVariantElementBindings[binding.id]
        }
      }

      delete appState.abVariants[variantId]
      normalizeAbVariantOrder(appState, abTestId)
      normalizeAbVariantTraffic(appState, abTestId)
      setState(next)
      return variantId
    },

    setAbVariantTraffic(applicationId, variantId, trafficPercent) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const variant = appState.abVariants?.[variantId]
      if (!variant) return null
      const value = Math.max(0, Math.min(100, Number(trafficPercent) || 0))
      variant.trafficPercent = value
      variant.updatedAt = nowIso()
      setState(next)
      return variantId
    },

    addAbVariantElement(applicationId, variantId, elementId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const variant = appState.abVariants?.[variantId]
      if (!variant) return null
      const element = appState.elements?.[elementId]
      if (!element) return null
      if (element.status !== 'published') throw new Error('Only published elements can be used in AB variants')

      const createdAt = nowIso()
      const existing = Object.values(appState.abVariantElementBindings || {})
        .filter((b) => b.abVariantId === variantId)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
      const nextOrder = existing.length ? (existing.at(-1).order || 0) + 1 : 1

      const id = randomId()
      appState.abVariantElementBindings[id] = {
        id,
        applicationId,
        abVariantId: variantId,
        elementId,
        conditionExpression: String(values?.conditionExpression || '').trim(),
        order: nextOrder,
        createdAt,
        updatedAt: createdAt,
      }

      setState(next)
      return id
    },

    removeAbVariantElement(applicationId, bindingId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      if (!appState.abVariantElementBindings?.[bindingId]) return null
      delete appState.abVariantElementBindings[bindingId]
      setState(next)
      return bindingId
    },

    addSegmentationToDraft(applicationId, groupConfigId, segmentationId, options = {}) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const draft = getGroupConfigDraftVersion(appState, groupConfigId)
      if (!draft) return null

      const alreadyAdded = Object.values(appState.groupSegments).some(
        (seg) =>
          seg.groupConfigVersionId === draft.id &&
          String(seg.segmentationId) === String(segmentationId),
      )
      if (alreadyAdded) {
        throw new Error('This segmentation is already added to the draft')
      }

      const createdAt = nowIso()
      const groupSegmentId = String(options.groupSegmentId || '').trim() || randomId()
      const priority =
        Number.isFinite(options.priority) && options.priority > 0
          ? options.priority
          : getNextPriority(appState, draft.id)

      appState.groupSegments[groupSegmentId] = {
        id: groupSegmentId,
        applicationId,
        groupConfigVersionId: draft.id,
        segmentKey: String(options.segmentKey || '').trim() || randomId(),
        segmentationId,
        priority,
        isEnabled: options.isEnabled !== undefined ? Boolean(options.isEnabled) : true,
        isDefault: options.isDefault !== undefined ? Boolean(options.isDefault) : false,
        createdAt: options.createdAt || createdAt,
        updatedAt: options.updatedAt || createdAt,
      }

      normalizePriorities(appState, draft.id)
      setState(next)
      return groupSegmentId
    },

    setGroupSegmentEnabled(applicationId, groupSegmentId, isEnabled) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const seg = appState.groupSegments[groupSegmentId]
      if (!seg) return null
      seg.isEnabled = Boolean(isEnabled)
      seg.updatedAt = nowIso()
      setState(next)
      return groupSegmentId
    },

    setGroupSegmentDefault(applicationId, groupSegmentId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const seg = appState.groupSegments[groupSegmentId]
      if (!seg) return null
      ensureSingleDefaultGroupSegment(appState, seg.groupConfigVersionId, groupSegmentId)
      seg.updatedAt = nowIso()
      setState(next)
      return groupSegmentId
    },

    moveGroupSegment(applicationId, groupSegmentId, direction) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const target = appState.groupSegments[groupSegmentId]
      if (!target) return null

      const segments = Object.values(appState.groupSegments)
        .filter((seg) => seg.groupConfigVersionId === target.groupConfigVersionId)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))

      const idx = segments.findIndex((seg) => seg.id === groupSegmentId)
      if (idx < 0) return null

      const swapWithIdx = direction === 'up' ? idx - 1 : idx + 1
      const other = segments[swapWithIdx]
      if (!other) return null

      const tmp = target.priority
      target.priority = other.priority
      other.priority = tmp
      target.updatedAt = nowIso()
      other.updatedAt = nowIso()

      normalizePriorities(appState, target.groupConfigVersionId)
      setState(next)
      return groupSegmentId
    },

    removeGroupSegment(applicationId, groupSegmentId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const seg = appState.groupSegments[groupSegmentId]
      if (!seg) return null

      for (const binding of Object.values(appState.elementBindings)) {
        if (binding.groupSegmentId === groupSegmentId) {
          delete appState.elementBindings[binding.id]
        }
      }

      delete appState.groupSegments[groupSegmentId]

      normalizePriorities(appState, seg.groupConfigVersionId)

      const remaining = Object.values(appState.groupSegments)
        .filter((s) => s.groupConfigVersionId === seg.groupConfigVersionId)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))

      if (remaining.length > 0 && seg.isDefault) {
        ensureSingleDefaultGroupSegment(appState, seg.groupConfigVersionId, remaining[0].id)
      }

      setState(next)
      return groupSegmentId
    },

    createDynamicRule(applicationId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const createdAt = nowIso()
      const dynamicRuleId = randomId()

      const type = String(values?.type || 'Value')
      if (!['Value', 'MLRule', 'Cyclic', 'ETL'].includes(type)) {
        throw new Error('Invalid dynamic rule type')
      }

      const name = String(values?.name || '').trim()
      if (!name) throw new Error('Dynamic rule name is required')

      appState.dynamicRules[dynamicRuleId] = {
        id: dynamicRuleId,
        applicationId,
        name,
        type,
        description: String(values?.description || '').trim(),
        placeholder: String(values?.placeholder || '').trim(),
        isEnabled: values?.isEnabled !== false,
        createdAt,
        updatedAt: createdAt,
      }

      setState(next)
      return dynamicRuleId
    },

    updateDynamicRule(applicationId, dynamicRuleId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const existing = appState.dynamicRules?.[dynamicRuleId]
      if (!existing) return null

      if (values?.name !== undefined) {
        existing.name = String(values.name || '').trim()
      }
      if (!existing.name) throw new Error('Dynamic rule name is required')

      if (values?.type !== undefined) {
        const type = String(values.type || '').trim()
        if (!['Value', 'MLRule', 'Cyclic', 'ETL'].includes(type)) {
          throw new Error('Invalid dynamic rule type')
        }
        existing.type = type
      }

      if (values?.description !== undefined) {
        existing.description = String(values.description || '').trim()
      }
      if (values?.placeholder !== undefined) {
        existing.placeholder = String(values.placeholder || '').trim()
      }
      if (values?.isEnabled !== undefined) {
        existing.isEnabled = Boolean(values.isEnabled)
      }

      existing.updatedAt = nowIso()
      setState(next)
      return dynamicRuleId
    },

    setDynamicRuleEnabled(applicationId, dynamicRuleId, isEnabled) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const existing = appState.dynamicRules?.[dynamicRuleId]
      if (!existing) return null

      existing.isEnabled = Boolean(isEnabled)
      existing.updatedAt = nowIso()
      setState(next)
      return dynamicRuleId
    },

    deleteDynamicRule(applicationId, dynamicRuleId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      if (!appState.dynamicRules?.[dynamicRuleId]) return null

      const usageCount = getDynamicRuleUsageCount(appState, dynamicRuleId)
      if (usageCount > 0) {
        throw new Error('Cannot delete dynamic rule that is used in elements')
      }

      delete appState.dynamicRules[dynamicRuleId]
      setState(next)
      return dynamicRuleId
    },

    createElement(applicationId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const elementId = randomId()
      const createdAt = nowIso()

      const rawJson = String(values?.jsonConfig || '').trim()
      let parsedJson = null
      if (rawJson) {
        parsedJson = safeJsonParse(rawJson, undefined)
        if (parsedJson === undefined) {
          throw new Error('Invalid JSON config')
        }
      }

      const status = String(values?.status || 'draft')
      if (!['draft', 'published', 'archived'].includes(status)) {
        throw new Error('Invalid element status')
      }

      appState.elements[elementId] = {
        id: elementId,
        applicationId,
        name: String(values?.name || '').trim(),
        elementType: String(values?.elementType || 'advertising').trim(),
        version: Number.isFinite(values?.version) ? values.version : 1,
        jsonConfig: parsedJson,
        rawJsonConfig: rawJson,
        text: String(values?.text || ''),
        dynamicRuleIds: normalizeDynamicRuleIds(appState, values?.dynamicRuleIds),
        status,
        publishedAt: status === 'published' ? createdAt : null,
        archivedAt: status === 'archived' ? createdAt : null,
        createdAt,
        updatedAt: createdAt,
      }

      setState(next)
      return elementId
    },

    updateElement(applicationId, elementId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const element = appState.elements[elementId]
      if (!element) return null

      if (values?.name !== undefined) element.name = String(values.name || '').trim()
      if (values?.elementType !== undefined) element.elementType = String(values.elementType || '').trim()
      if (values?.version !== undefined) {
        element.version = Number.isFinite(values.version) ? values.version : element.version
      }
      if (values?.text !== undefined) element.text = String(values.text || '')
      if (values?.dynamicRuleIds !== undefined) {
        element.dynamicRuleIds = normalizeDynamicRuleIds(appState, values.dynamicRuleIds)
      }

      if (values?.jsonConfig !== undefined) {
        const rawJson = String(values.jsonConfig || '').trim()
        let parsedJson = null
        if (rawJson) {
          parsedJson = safeJsonParse(rawJson, undefined)
          if (parsedJson === undefined) {
            throw new Error('Invalid JSON config')
          }
        }
        element.rawJsonConfig = rawJson
        element.jsonConfig = parsedJson
      }

      element.updatedAt = nowIso()
      setState(next)
      return elementId
    },

    publishElement(applicationId, elementId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const element = appState.elements[elementId]
      if (!element) return null
      if (element.status === 'archived') throw new Error('Cannot publish archived element')
      element.status = 'published'
      element.publishedAt = element.publishedAt || nowIso()
      element.updatedAt = nowIso()
      setState(next)
      return elementId
    },

    unpublishElement(applicationId, elementId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const element = appState.elements[elementId]
      if (!element) return null
      if (element.status !== 'published') return elementId
      const usageCount = getElementUsageCount(appState, elementId)
      if (usageCount > 0) throw new Error('Cannot unpublish element that is used in segments')
      const usageInTests = getElementUsageCountInAbTests(appState, elementId)
      if (usageInTests > 0) throw new Error('Cannot unpublish element that is used in AB tests')
      element.status = 'draft'
      element.updatedAt = nowIso()
      setState(next)
      return elementId
    },

    archiveElement(applicationId, elementId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const element = appState.elements[elementId]
      if (!element) return null
      element.status = 'archived'
      element.archivedAt = element.archivedAt || nowIso()
      element.updatedAt = nowIso()
      setState(next)
      return elementId
    },

    deleteElement(applicationId, elementId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      if (!appState.elements[elementId]) return null

      const usageCount = getElementUsageCount(appState, elementId)
      if (usageCount > 0) {
        throw new Error('Cannot delete element that is used in segments')
      }
      const usageInTests = getElementUsageCountInAbTests(appState, elementId)
      if (usageInTests > 0) {
        throw new Error('Cannot delete element that is used in AB tests')
      }

      delete appState.elements[elementId]

      setState(next)
      return elementId
    },

    bindElement(applicationId, groupSegmentId, elementId, values = {}, options = {}) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const groupSegment = appState.groupSegments[groupSegmentId]
      if (!groupSegment) return null
      const element = appState.elements[elementId]
      if (!element) return null
      if (element.status !== 'published') {
        throw new Error('Only published elements can be attached to segments')
      }

      const createdAt = nowIso()
      const existingBindings = Object.values(appState.elementBindings)
        .filter((binding) => binding.groupSegmentId === groupSegmentId)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
      const nextOrder = existingBindings.length ? (existingBindings.at(-1).order || 0) + 1 : 1

      const bindingId = String(options.bindingId || '').trim() || randomId()
      if (appState.elementBindings[bindingId]) {
        throw new Error('Binding id already exists')
      }
      appState.elementBindings[bindingId] = {
        id: bindingId,
        applicationId,
        groupSegmentId,
        elementId,
        conditionExpression: String(values?.conditionExpression || '').trim(),
        order: Number.isFinite(options.order) ? options.order : nextOrder,
        createdAt: options.createdAt || createdAt,
        updatedAt: options.updatedAt || createdAt,
      }

      setState(next)
      return bindingId
    },

    updateBinding(applicationId, bindingId, values) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const binding = appState.elementBindings[bindingId]
      if (!binding) return null
      if (values?.elementId !== undefined) {
        const nextElementId = String(values.elementId || '').trim()
        const nextElement = appState.elements[nextElementId]
        if (!nextElement) {
          throw new Error('Element not found')
        }
        if (nextElement.status !== 'published') {
          throw new Error('Only published elements can be attached to segments')
        }
        binding.elementId = nextElementId
      }
      binding.conditionExpression = String(
        values?.conditionExpression ?? binding.conditionExpression,
      ).trim()
      binding.updatedAt = nowIso()
      setState(next)
      return bindingId
    },

    removeBinding(applicationId, bindingId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const binding = appState.elementBindings[bindingId]
      if (!binding) return null
      delete appState.elementBindings[bindingId]
      setState(next)
      return bindingId
    },

    moveBinding(applicationId, bindingId, direction) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const binding = appState.elementBindings[bindingId]
      if (!binding) return null

      const list = Object.values(appState.elementBindings)
        .filter((b) => b.groupSegmentId === binding.groupSegmentId)
        .sort((a, b) => (a.order || 0) - (b.order || 0))

      const idx = list.findIndex((b) => b.id === bindingId)
      if (idx < 0) return null

      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      const other = list[swapIdx]
      if (!other) return null

      const tmp = binding.order
      binding.order = other.order
      other.order = tmp
      binding.updatedAt = nowIso()
      other.updatedAt = nowIso()

      setState(next)
      return bindingId
    },

    releaseGroupConfigDraft(applicationId, groupConfigId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      const draft = getGroupConfigDraftVersion(appState, groupConfigId)
      if (!draft) return null

      const releasedAt = nowIso()
      draft.status = 'released'
      draft.updatedAt = releasedAt

      const newDraftId = randomId()
      appState.groupConfigVersions[newDraftId] = {
        id: newDraftId,
        applicationId,
        groupConfigId,
        status: 'draft',
        createdAt: releasedAt,
        updatedAt: releasedAt,
        parentVersionId: draft.id,
      }

      cloneVersionGraph(appState, draft.id, newDraftId)
      normalizePriorities(appState, newDraftId)

      const newDraftSegments = Object.values(appState.groupSegments).filter(
        (seg) => seg.groupConfigVersionId === newDraftId,
      )
      const defaultSeg = newDraftSegments.find((seg) => seg.isDefault) || newDraftSegments[0]
      if (defaultSeg) ensureSingleDefaultGroupSegment(appState, newDraftId, defaultSeg.id)

      setState(next)
      return { releasedVersionId: draft.id, newDraftVersionId: newDraftId }
    },

    deleteGroupConfig(applicationId, groupConfigId) {
      const next = clone(_state)
      const appState = ensureApp(next, applicationId)
      if (!appState.groupConfigs[groupConfigId]) return null

      const versionIds = Object.values(appState.groupConfigVersions)
        .filter((v) => v.groupConfigId === groupConfigId)
        .map((v) => v.id)

      const versionSet = new Set(versionIds)

      for (const seg of Object.values(appState.groupSegments)) {
        if (!versionSet.has(seg.groupConfigVersionId)) continue
        for (const binding of Object.values(appState.elementBindings)) {
          if (binding.groupSegmentId === seg.id) {
            delete appState.elementBindings[binding.id]
          }
        }
        delete appState.groupSegments[seg.id]
      }

      for (const versionId of versionIds) {
        delete appState.groupConfigVersions[versionId]
      }

      delete appState.groupConfigs[groupConfigId]

      setState(next)
      return groupConfigId
    },
  }
}

